// Kimi AI Service for Blog Content Generation
// Uses Moonshot AI (Kimi) API - with improved markdown parsing

const KIMI_API_KEY = 'sk-mE06M4pqgmG7OQ744IIutYa7seRAmND0yGRan5fJCaEFTpnp';
const KIMI_API_URL = 'https://api.moonshot.ai/v1/chat/completions';

// Enhanced System Prompt for better structured output
const SYSTEM_PROMPT = `You are a professional content writer for Qingshun Electronics Technology Co., Ltd., a leading manufacturer of electronic connectors and cable assemblies based in China.

IMPORTANT: When writing content, use proper markdown formatting:
- Use # for main title
- Use ## for section headings
- Use **text** for bold/emphasis
- Use - for bullet points
- Use : for key-value pairs or definitions

Create SEO-optimized blog posts with:
1. Compelling title with primary keyword
2. 5-7 well-structured sections with H2 headings
3. Bullet points using - for lists
4. Bold text with ** for key terms
5. Strong call-to-action at the end

The company specializes in:
- Alternative connector solutions (TE, Amphenol, Molex, etc.)
- Cost savings of 50-70% compared to original brands
- Cable assemblies and wire harnesses
- Automotive, industrial, medical, telecommunications applications
- RoHS compliance and ISO certifications
- 14 years of manufacturing experience
- Vertical integration for quality control

Write in professional tone with data-driven insights and practical examples.`;

// Generate a complete blog post
export const generateBlogContent = async (topic, category = 'General') => {
  const prompt = `Write a complete, SEO-optimized blog post about: "${topic}"

Requirements:
- Title should be compelling and include primary keyword (use # Title format)
- Meta description (150-160 characters) for SEO
- 5-7 sections with ## H2 headings
- Each section should have:
  - 3-4 paragraphs OR bullet points using -
  - **Bold text** for key terms and important points
  - Use : for definitions or key-value pairs
- Include relevant industry keywords naturally
- End with a strong call-to-action
- Format using proper markdown

Category: ${category}

Write the blog post now with proper markdown formatting:`;

  return callKimiAPI(prompt);
};

// Generate blog post ideas based on category
export const generateBlogIdeas = async (category = 'General', count = 5) => {
  const prompt = `Generate ${count} compelling blog post ideas for ${category} related to electronic connectors, cable assemblies, or manufacturing. 

Format each idea as:
**Title**: Catchy title with keyword
**Description**: 2-3 sentences describing the post
**Keywords**: 3-4 target keywords

Use this format:
1. **Title**: ... | Description: ... | Keywords: ...

Make each idea highly engaging and SEO-friendly.`;

  return callKimiAPI(prompt);
};

// Expand bullet points into detailed paragraphs
export const expandContent = async (bulletPoints, sectionHeading) => {
  const prompt = `Expand these bullet points into detailed paragraphs for a blog section titled "${sectionHeading}":

${bulletPoints}

Requirements:
- Create 3-4 detailed paragraphs
- Use **bold text** for key terms
- Maintain the key points from the bullets
- Add supporting details and examples
- Make it engaging and informative

Write with proper markdown formatting. Use **text** for emphasis.`;

  return callKimiAPI(prompt);
};

// Generate SEO meta tags
export const generateSEOMeta = async (title, content) => {
  const prompt = `For a blog post with the title "${title}", generate:
1. SEO title (50-60 characters, include primary keyword)
2. Meta description (150-160 characters, compelling and include keywords)
3. 5-7 related keywords/tags
4. URL slug (kebab-case, include primary keyword)

Content summary: ${content.substring(0, 500)}...

Format as:
SEO Title: ...
Meta Description: ...
Keywords: ...
URL Slug: ...`;

  return callKimiAPI(prompt);
};

// Improve existing content
export const improveContent = async (content, improvementType = 'clarity') => {
  const prompt = `Improve this blog content for ${improvementType}:

${content}

Requirements:
- Maintain the original meaning and structure
- Enhance readability and engagement
- Use **bold text** for important terms
- Ensure natural flow between paragraphs
- Keep SEO keywords where relevant
- Make it more compelling for readers

Write the improved version with proper markdown formatting. Use **text** for emphasis.`;

  return callKimiAPI(prompt);
};

// Generate social media snippets
export const generateSocialSnippets = async (title, excerpt) => {
  const prompt = `Generate social media promotional snippets for this blog post:

Title: ${title}
Excerpt: ${excerpt}

Create:
1. LinkedIn post (professional tone, 150-200 characters with hashtags)
2. Twitter/X post (engaging, 100-150 characters with hashtags)
3. Email subject line (compelling, 40-60 characters)

Format as:
LinkedIn: ...
Twitter: ...
Email: ...`;

  return callKimiAPI(prompt);
};

// Main API call function
const callKimiAPI = async (userPrompt, model = 'moonshot-v1-8k') => {
  try {
    const response = await fetch(KIMI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${KIMI_API_KEY}`
      },
      body: JSON.stringify({
        model: model,
        messages: [
          {
            role: 'system',
            content: SYSTEM_PROMPT
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        temperature: 0.7,
        max_tokens: 4000
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.choices && data.choices[0] && data.choices[0].message) {
      return {
        success: true,
        content: data.choices[0].message.content.trim(),
        usage: data.usage || null
      };
    } else {
      throw new Error('Invalid response format from Kimi API');
    }
  } catch (error) {
    console.error('Kimi API Error:', error);
    return {
      success: false,
      error: error.message || 'Failed to generate content',
      content: null
    };
  }
};

// Enhanced markdown parser - converts markdown to structured data
export const parseGeneratedContent = (content) => {
  const lines = content.split('\n');
  const result = {
    title: '',
    metaDescription: '',
    sections: []
  };

  let currentSection = null;
  let metaDescription = [];

  for (const line of lines) {
    const trimmed = line.trim();
    
    // Skip empty lines
    if (!trimmed) {
      if (currentSection) {
        currentSection.paragraphs.push('');
      }
      continue;
    }
    
    // Main title (# Title)
    if (trimmed.startsWith('# ') && !trimmed.startsWith('## ')) {
      result.title = trimmed.replace(/^#\s*/, '').trim();
      continue;
    }
    
    // Section headings (## Heading)
    if (trimmed.startsWith('## ')) {
      if (currentSection) {
        result.sections.push(currentSection);
      }
      currentSection = {
        heading: trimmed.replace(/^##\s*/, '').trim(),
        paragraphs: [],
        isList: false
      };
      continue;
    }
    
    // If no section yet, treat as intro
    if (!currentSection) {
      // Skip meta description line if exists
      if (trimmed.toLowerCase().startsWith('meta description:')) {
        metaDescription.push(trimmed.split(':').slice(1).join(':').trim());
      } else if (!result.title && trimmed) {
        result.title = trimmed;
      } else if (trimmed && !trimmed.startsWith('#')) {
        metaDescription.push(trimmed);
      }
      continue;
    }
    
    // Bullet points (- item or * item)
    if (trimmed.match(/^[-*•]\s+/)) {
      currentSection.isList = true;
      const item = trimmed.replace(/^[-*•]\s+/, '');
      currentSection.paragraphs.push({ type: 'list', content: item });
    }
    // Numbered lists (1. item)
    else if (trimmed.match(/^\d+\.\s+/)) {
      currentSection.isList = true;
      const item = trimmed.replace(/^\d+\.\s+/, '');
      currentSection.paragraphs.push({ type: 'list', content: item });
    }
    // Regular paragraph
    else {
      currentSection.paragraphs.push({ type: 'paragraph', content: trimmed });
    }
  }

  // Add last section
  if (currentSection) {
    result.sections.push(currentSection);
  }

  // Build meta description from intro
  if (metaDescription.length > 0) {
    result.metaDescription = metaDescription.filter(p => p && !p.startsWith('**')).slice(0, 2).join(' ');
  }

  return result;
};

// Enhanced HTML generator with proper markdown support
export const generateHTMLFromContent = (structuredContent, mainImage = null) => {
  let html = '';

  // Helper function to convert markdown to HTML
  const markdownToHtml = (text) => {
    if (!text) return '';
    
    // Convert **bold** to <strong>
    let result = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    
    // Convert *italic* to <em>
    result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
    
    // Convert inline code `code`
    result = result.replace(/`(.+?)`/g, '<code>$1</code>');
    
    // Convert links [text](url)
    result = result.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
    
    return result;
  };

  // Add main image if provided
  if (mainImage) {
    html += `<img src="${mainImage}" alt="${structuredContent.title || 'Blog Post Image'}" style="width: 100%; max-width: 800px; height: auto; border-radius: 12px; margin: 20px 0; display: block; box-shadow: 0 10px 30px rgba(0,0,0,0.15);" />\n\n`;
  }

  // Add title
  if (structuredContent.title) {
    html += `<h1 style="font-size: 2.5rem; font-weight: 800; color: #1a1a1a; margin-bottom: 1rem; line-height: 1.2;">${markdownToHtml(structuredContent.title)}</h1>\n\n`;
  }

  // Add meta description
  if (structuredContent.metaDescription) {
    html += `<p style="font-size: 1.25rem; color: #666; margin-bottom: 2rem; font-style: italic; line-height: 1.6;">${markdownToHtml(structuredContent.metaDescription)}</p>\n\n`;
  }

  // Add sections
  const emojis = ['🚀', '✨', '🎯', '💡', '🔬', '⚡', '🏆', '💼', '🌟', '📊'];
  
  structuredContent.sections.forEach((section, index) => {
    const emoji = emojis[index % emojis.length];
    
    // Section heading
    html += `<h2 style="font-size: 1.75rem; font-weight: 700; color: #dc2626; margin-top: 2rem; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 2px solid #fee2e2; display: flex; align-items: center; gap: 0.5rem;">
      <span>${emoji}</span> ${markdownToHtml(section.heading)}
    </h2>\n`;
    
    if (section.paragraphs && section.paragraphs.length > 0) {
      // Check if it's a list section
      const hasList = section.paragraphs.some(p => p.type === 'list');
      
      if (hasList) {
        // Render as list
        html += `<ul style="list-style: none; padding: 0; margin: 1rem 0;">\n`;
        
        section.paragraphs.forEach(p => {
          if (p.type === 'list') {
            // Check for key:value format
            if (p.content.includes(':')) {
              const [key, ...valueParts] = p.content.split(':');
              html += `  <li style="background: #f9fafb; margin: 0.75rem 0; padding: 1rem; border-radius: 8px; border-left: 4px solid #dc2626;">
                <strong style="color: #dc2626; font-size: 1.1rem;">${markdownToHtml(key.trim())}:</strong>
                <span style="color: #374151;">${markdownToHtml(valueParts.join(':').trim())}</span>
              </li>\n`;
            } else {
              html += `  <li style="background: #f9fafb; margin: 0.75rem 0; padding: 1rem; border-radius: 8px; border-left: 4px solid #dc2626; display: flex; align-items: flex-start; gap: 0.75rem;">
                <span style="color: #dc2626; font-weight: bold;">✓</span>
                <span style="color: #374151; line-height: 1.6;">${markdownToHtml(p.content)}</span>
              </li>\n`;
            }
          } else if (p.type === 'paragraph' && p.content) {
            html += `  <li style="background: #f9fafb; margin: 0.75rem 0; padding: 1rem; border-radius: 8px; line-height: 1.7; color: #374151;">${markdownToHtml(p.content)}</li>\n`;
          }
        });
        
        html += `</ul>\n\n`;
      } else {
        // Render as paragraphs
        section.paragraphs.forEach(p => {
          if (p.content && p.type === 'paragraph') {
            html += `<p style="font-size: 1.1rem; line-height: 1.8; margin: 1rem 0; color: #374151;">${markdownToHtml(p.content)}</p>\n`;
          }
        });
        html += '\n';
      }
    }
  });

  // Add CTA footer
  html += `<div style="background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); color: white; padding: 30px; border-radius: 16px; text-align: center; margin: 40px 0; box-shadow: 0 10px 25px rgba(220, 38, 38, 0.3);">
    <h3 style="font-size: 1.5rem; margin: 0 0 0.75rem 0; font-weight: 700;">💬 Ready to Transform Your Connector Strategy?</h3>
    <p style="font-size: 1.1rem; margin: 0 0 1.5rem 0; opacity: 0.95;">Get expert advice on cost-saving alternatives without compromising quality!</p>
    <a href="/contact" style="display: inline-block; background: white; color: #dc2626; padding: 14px 32px; border-radius: 8px; font-weight: 700; text-decoration: none; font-size: 1.1rem; transition: transform 0.2s, box-shadow 0.2s;" onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 15px rgba(0,0,0,0.2)'" onmouseout="this.style.transform='none'; this.style.boxShadow='none'">
      Get Free Consultation →
    </a>
  </div>`;

  return html;
};

// Simple wrapper for applying to admin
export const applyGeneratedPost = (structuredContent, mainImage = null) => {
  return {
    title: structuredContent.title || 'Untitled Post',
    content: generateHTMLFromContent(structuredContent, mainImage),
    metaDescription: structuredContent.metaDescription || ''
  };
};

export default {
  generateBlogContent,
  generateBlogIdeas,
  expandContent,
  generateSEOMeta,
  improveContent,
  generateSocialSnippets,
  parseGeneratedContent,
  generateHTMLFromContent,
  applyGeneratedPost
};